import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { usePlan } from '../../context/PlanContext';
import { toast } from 'sonner';
import { settingsApi } from '../../services';

/**
 * Billing Settings Page
 * Manage subscription plans and billing
 */
const BillingSettings = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { plan, features, loading: planLoading, refreshPlan } = usePlan();
  
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [seatCount, setSeatCount] = useState(2);

  const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

  useEffect(() => {
    fetchPlans();
    
    // Check if returning from Stripe checkout
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      pollPaymentStatus(sessionId);
    }
  }, [searchParams]);

  const fetchPlans = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/subscriptions/plans`);
      setPlans(response.data.plans);
    } catch (error) {
      console.error('Failed to fetch plans:', error);
      toast.error('Kunne ikke laste planer');
    }
  };

  const pollPaymentStatus = async (sessionId, attempt = 0) => {
    const maxAttempts = 5;
    
    if (attempt >= maxAttempts) {
      toast.error('Betalingsstatus sjekk tok for lang tid. Vennligst kontakt support.');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_URL}/api/subscriptions/checkout/status/${sessionId}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data.payment_status === 'paid' || response.data.status === 'complete') {
        toast.success('🎉 Betaling vellykket! Abonnementet ditt er aktivert.');
        refreshPlan();
        
        // Clear session_id from URL
        navigate('/settings/billing', { replace: true });
        return;
      } else if (response.data.status === 'expired') {
        toast.error('Betalingssesjon utløpt. Vennligst prøv igjen.');
        return;
      }

      // Still pending, poll again
      setTimeout(() => pollPaymentStatus(sessionId, attempt + 1), 2000);
    } catch (error) {
      console.error('Payment status check failed:', error);
      toast.error('Kunne ikke sjekke betalingsstatus');
    }
  };

  const handlePlanChange = async (planType) => {
    setLoading(true);
    setSelectedPlan(planType);

    try {
      const token = localStorage.getItem('token');

      // For nå: Bytt plan gratis (uten betaling)
      const response = await axios.post(
        `${API_URL}/api/subscriptions/change-plan`,
        {
          plan_type: planType,
          seat_count: planType === 'TEAM' || planType === 'ENTERPRISE' ? seatCount : 1
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      toast.success(`✅ Plan endret til ${planType}!`);
      await refreshPlan();
      setSelectedPlan(null);
    } catch (error) {
      console.error('Plan change failed:', error);
      toast.error(error.response?.data?.detail || 'Kunne ikke bytte plan');
    } finally {
      setLoading(false);
    }
  };

  const calculatePrice = (planData) => {
    if (planData.plan_type === 'FREE') {
      return '0 kr';
    } else if (planData.plan_type === 'PRO') {
      return `${planData.pricing.price_per_month} kr/mnd`;
    } else {
      const pricePerSeat = planData.pricing.price_per_seat_per_month;
      const total = pricePerSeat * seatCount;
      return `${total.toLocaleString()} kr/mnd (${pricePerSeat} kr × ${seatCount} advokater)`;
    }
  };

  const getFeaturesList = (planFeatures) => {
    const features = [];
    
    if (planFeatures.can_create_own_cases) features.push('Opprett egne saker');
    if (planFeatures.marketplace_access) features.push('Marketplace tilgang');
    if (planFeatures.can_add_team_members) features.push('Team samarbeid');
    if (planFeatures.has_team_dashboard) features.push('Team dashboard');
    if (planFeatures.can_share_documents) features.push('Dokumentdeling');
    if (planFeatures.has_case_chat) features.push('Sak-chat');
    if (planFeatures.has_departments) features.push('Avdelinger');
    if (planFeatures.has_sso) features.push('SSO/SCIM');
    
    return features;
  };

  if (planLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F4F2EE] py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Back Navigation */}
        <div className="mb-8 flex items-center justify-between">
          <button
            onClick={() => window.history.back()}
            className="flex items-center gap-2 text-slate-600 hover:text-slate-900 transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            <span className="font-medium">Tilbake</span>
          </button>
          <a
            href="/lexy/dashboard"
            className="text-sm text-indigo-600 hover:text-indigo-700 font-medium"
          >
            Gå til dashboard →
          </a>
        </div>

        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Abonnement & Fakturering
          </h1>
          <p className="text-xl text-gray-600">
            Velg planen som passer ditt behov
          </p>
        </div>

        {/* Current Plan Badge */}
        {plan && (
          <div className="bg-white rounded-lg shadow-sm p-6 mb-8">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Nåværende plan</p>
                <p className="text-2xl font-bold text-gray-900">{plan.plan_type}</p>
                {plan.plan_type !== 'FREE' && (
                  <p className="text-sm text-gray-500">
                    {plan.active_lawyers} / {plan.seat_count} aktive advokater
                  </p>
                )}
              </div>
              {plan.plan_type !== 'FREE' && (
                <button
                  onClick={() => toast.info('Billing portal kommer snart')}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Administrer abonnement
                </button>
              )}
            </div>
          </div>
        )}

        {/* Plans Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {plans.map((planData) => {
            const isCurrent = planData.plan_type === plan?.plan_type;
            const featuresList = getFeaturesList(planData.features);

            return (
              <div
                key={planData.plan_type}
                className={`bg-white rounded-lg shadow-lg overflow-hidden ${
                  isCurrent ? 'ring-2 ring-indigo-600' : ''
                }`}
              >
                {/* Plan Header */}
                <div className={`p-6 ${isCurrent ? 'bg-indigo-50' : 'bg-gray-50'}`}>
                  <h3 className="text-2xl font-bold text-gray-900">{planData.name}</h3>
                  <p className="text-gray-600 mt-2">{planData.pricing.description}</p>
                  
                  {isCurrent && (
                    <span className="inline-block mt-2 px-3 py-1 bg-indigo-600 text-white text-sm font-semibold rounded-full">
                      Nåværende plan
                    </span>
                  )}
                </div>

                {/* Pricing */}
                <div className="p-6">
                  <div className="text-3xl font-bold text-gray-900 mb-4">
                    {calculatePrice(planData)}
                  </div>

                  {/* Seat Selector for TEAM/ENTERPRISE */}
                  {(planData.plan_type === 'TEAM' || planData.plan_type === 'ENTERPRISE') && (
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Antall advokater
                      </label>
                      <input
                        type="number"
                        min={planData.pricing.min_seats || 2}
                        value={seatCount}
                        onChange={(e) => setSeatCount(parseInt(e.target.value) || 2)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      />
                    </div>
                  )}

                  {/* Features List */}
                  <ul className="space-y-2 mb-6">
                    {featuresList.map((feature, idx) => (
                      <li key={idx} className="flex items-start">
                        <svg className="h-5 w-5 text-green-500 mr-2 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        <span className="text-gray-700">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  {/* Action Button */}
                  {!isCurrent ? (
                    <button
                      onClick={() => handlePlanChange(planData.plan_type)}
                      disabled={loading && selectedPlan === planData.plan_type}
                      className="w-full py-3 px-4 bg-indigo-600 text-white font-semibold rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      {loading && selectedPlan === planData.plan_type ? 'Bytter plan...' : 'Bytt til denne planen'}
                    </button>
                  ) : (
                    <button
                      disabled
                      className="w-full py-3 px-4 bg-green-100 text-green-700 font-semibold rounded-lg cursor-not-allowed"
                    >
                      ✓ Aktiv plan
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* FAQ / Support */}
        <div className="mt-12 text-center">
          <p className="text-gray-600">
            Har du spørsmål om planene?{' '}
            <button
              onClick={() => toast.info('Kontakt support på support@lexy.no')}
              className="text-indigo-600 hover:text-indigo-700 font-semibold"
            >
              Kontakt oss
            </button>
          </p>
        </div>
      </div>
    </div>
  );
};

export default BillingSettings;
